import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  HbrCard,
  HbrIcon,
  HbrTag,
} from '@harbor/elements-react-standalone';
import {
  statusOk,
  statusGeneralDormant,
  statusInProgress,
} from '@harbor/elements/icons';


import { RootState } from '../../support/store';
import { fetchItem } from '../../support/queries';
import { useMoodleCourses } from '../../hooks/useMoodleHooks';
import {
  selectMoodleCategoriesMap,
  selectExternalMoodleUserState,
} from '../../support/slices/moodleSlice';
import { useQuery } from '@tanstack/react-query'; //-tech-debt-react-query;

type MoodleCourse = {
  id: number;
  fullname?: string;
  shortname?: string;
  summary?: string;
  categoryid?: number | string;
  category_id?: number | string;
  category?: number | string;
};

type MoodleCategory = {
  id: number | string;
  name: string;
};

type CourseProgress = {
  email: string;
  course_id: number;
  moodle_user_id: number;
  completed_count: number;
  total_count: number;
  progress_percent: number;
  passed: boolean;
};

type CourseStatus = 'completed' | 'in-progress' | 'not-started';
type StatusFilter = 'all' | CourseStatus;
type SortOption =
  | 'status'
  | 'name-asc'
  | 'name-desc'
  | 'progress-desc'
  | 'progress-asc';

type EnrichedCourse = MoodleCourse & {
  progress: CourseProgress | null;
  status: CourseStatus;
  percent: number;
};

function courseCategoryId(course: MoodleCourse): string {
  return String(course.categoryid ?? course.category_id ?? course.category ?? '');
}

function stripHtml(html?: string): string {
  if (!html) return '';
  return html
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&#39;/gi, "'")
    .replace(/&quot;/gi, '"')
    .replace(/\s+/g, ' ')
    .trim();
}

function truncate(text: string, max = 220): string {
  if (!text) return '';
  if (text.length <= max) return text;
  return `${text.slice(0, max).trim()}…`;
}

function matchesTrainTheTrainerTitle(course: MoodleCourse): boolean {
  const fullname = String(course?.fullname ?? '').trim().toLowerCase();
  const shortname = String(course?.shortname ?? '').trim().toLowerCase();

  const matchesTrainer =
    fullname.includes('train the trainer') ||
    fullname.includes('train the trainer-old') ||
    shortname.includes('ttt') ||
    fullname.includes(' ttt') ||
    fullname.startsWith('ttt ');

  const isDeprecated =
    fullname.includes('(deprecated)') ||
    fullname.includes('-old') ||
    shortname.includes('-old');

  return matchesTrainer && !isDeprecated;
}

function getCourseStatus(progress?: CourseProgress | null): CourseStatus {
  const percent = Number(progress?.progress_percent ?? 0);
  const passed = !!progress?.passed;

  if (passed || percent >= 100) return 'completed';
  if (percent > 0) return 'in-progress';
  return 'not-started';
}

function getStatusRank(status: CourseStatus): number {
  switch (status) {
    case 'in-progress':
      return 0;
    case 'not-started':
      return 1;
    case 'completed':
      return 2;
    default:
      return 3;
  }
}

function getStatusUi(status: CourseStatus) {
  switch (status) {
    case 'completed':
      return {
        label: 'Completed',
        sentiment: 'success' as const,
        iconAsset: statusOk,
        cardSentiment: 'success',
        progressColor: 'var(--hbr-color-success)',
        metricBackground: 'var(--hbr-color-success-bg, #F4FAEF)',
        metricBorder: 'var(--hbr-color-success-bg, #F4FAEF)',
        metricIconBg: 'var(--hbr-color-success-bg, #F4FAEF)',
        metricIconColor: 'var(--hbr-color-success-icon, var(--hbr-color-success, #45991F))',
        metricValueColor: 'var(--hbr-color-success, #45991F)',
      };
    case 'in-progress':
      return {
        label: 'In Progress',
        sentiment: 'in-progress' as const,
        iconAsset: statusInProgress,
        cardSentiment: 'info',
        progressColor: 'var(--hbr-color-interact, #1D69CC)',
        metricBackground: 'var(--hbr-color-info-bg, #EEF5FE)',
        metricBorder: 'var(--hbr-color-info-bg, #EEF5FE)',
        metricIconBg: 'var(--hbr-color-info-bg, #EEF5FE)',
        metricIconColor: 'var(--hbr-color-info-icon, var(--hbr-color-interact, #1D69CC))',
        metricValueColor: 'var(--hbr-color-interact, #1D69CC)',
      };
    default:
      return {
        label: 'Not Started',
        sentiment: 'inactive' as const,
        iconAsset: statusGeneralDormant,
        cardSentiment: undefined,
        progressColor: 'var(--hbr-color-neutral-medium, #6F7680)',
        metricBackground: 'var(--hbr-color-neutral-bg-medium, #F7F7F7)',
        metricBorder: 'var(--hbr-color-neutral-bg-medium, #F7F7F7)',
        metricIconBg: 'var(--hbr-color-neutral-bg-weak, #E1E4E8)',
        metricIconColor: 'var(--hbr-color-neutral-icon-strong, #656C75)',
        metricValueColor: 'var(--hbr-color-neutral, #656C75)',
      };
  }
}

async function fetchCourseProgressByEmail(
  email: string,
  courseId: number
): Promise<CourseProgress | null> {
  if (!email || !courseId) return null;

  return fetchItem('api/moodle/cyber-academy/journey-map/course-progress-by-email', {
    email,
    course_id: courseId,
  });
}

function useTrainTheTrainerProgress(
  email: string | null | undefined,
  courseIds: number[]
) {
  return useQuery({
    queryKey: ['train-the-trainer-progress', email, courseIds],
    enabled: !!email && courseIds.length > 0,
    queryFn: async () => {
      const results = await Promise.all(
        courseIds.map(async (courseId) => {
          try {
            const progress = await fetchCourseProgressByEmail(email!, courseId);
            return [courseId, progress] as const;
          } catch {
            return [
              courseId,
              {
                email: email!,
                course_id: courseId,
                moodle_user_id: 0,
                completed_count: 0,
                total_count: 0,
                progress_percent: 0,
                passed: false,
              },
            ] as const;
          }
        })
      );

      return Object.fromEntries(results) as Record<number, CourseProgress | null>;
    },
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

type MetricCardProps = {
  title: string;
  value: number;
  background: string;
  borderColor: string;
  valueColor: string;
  iconAsset?: any;
  iconColor?: string;
  iconBg?: string;
};

function MetricCard({
                      title,
                      value,
                      background,
                      borderColor,
                      valueColor,
                      iconAsset,
                      iconColor,
                      iconBg,
                    }: MetricCardProps) {
  return (
    <HbrCard
      selectable={false}
      style={
        {
          '--width': '100%',
          '--min-height': '112px',
          '--max-width': '100%',
          '--border-radius': '8px',
          '--border-width': '2px',
          '--border-color': borderColor,
          '--box-shadow': 'none',
          background,
        } as React.CSSProperties
      }
    >
      <div className="hbr-css__layout-row hbr-css__layout-justify-between hbr-css__layout-align-start">
        <div className="hbr-css__layout-col-2xs">
          <div className="hbr-css__text-secondary-sm">{title}</div>
          <div
            className="hbr-css__text-primary-lg-bold"
            style={{ color: valueColor }}
          >
            {value}
          </div>
        </div>

        {iconAsset ? (
          <HbrIcon
            asset={iconAsset}
            style={
              {
                color: iconColor,
                backgroundColor: iconBg,
                fontSize: '20px',
                border: `4px solid ${iconBg}`,
                borderRadius: 'var(--hbr-border-radius-sm)',
              } as React.CSSProperties
            }
          />
        ) : null}
      </div>
    </HbrCard>
  );
}

export default function TrainTheTrainerPage() {
  const navigate = useNavigate();
  const provider = 'moodle';
  const instance = 'external';

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortBy, setSortBy] = useState<SortOption>('status');

  const { data: courses = [], isLoading, error } = useMoodleCourses(
    {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
    instance
  );

  const categoriesMap = useSelector(selectMoodleCategoriesMap);
  const externalMoodleUser = useSelector(selectExternalMoodleUserState);
  const sfEmail = useSelector((state: RootState) => state.user?.userData?.email);

  const email = externalMoodleUser?.data?.email || sfEmail || null;

  const allCategories = useMemo(
    () => Object.values(categoriesMap || {}) as MoodleCategory[],
    [categoriesMap]
  );

  const trainerCategoryIds = useMemo(() => {
    return allCategories
      .filter((cat) => String(cat?.name ?? '').trim().toLowerCase() === 'train the trainer')
      .map((cat) => String(cat.id));
  }, [allCategories]);

  const trainerCourses = useMemo(() => {
    const all = Array.isArray(courses) ? (courses as MoodleCourse[]) : [];

    if (trainerCategoryIds.length) {
      return all.filter((course) => trainerCategoryIds.includes(courseCategoryId(course)));
    }

    return all.filter(matchesTrainTheTrainerTitle);
  }, [courses, trainerCategoryIds]);

  const baseSortedCourses = useMemo(() => {
    return [...trainerCourses].sort((a, b) =>
      String(a.fullname ?? a.shortname ?? '').localeCompare(
        String(b.fullname ?? b.shortname ?? '')
      )
    );
  }, [trainerCourses]);

  const courseIds = useMemo(
    () => baseSortedCourses.map((course) => course.id),
    [baseSortedCourses]
  );

  const {
    data: progressByCourseId = {},
    isLoading: isProgressLoading,
  } = useTrainTheTrainerProgress(email, courseIds);

  const coursesWithProgress = useMemo<EnrichedCourse[]>(() => {
    return baseSortedCourses.map((course) => {
      const progress = progressByCourseId[course.id] ?? null;
      const status = getCourseStatus(progress);
      const percent = Number(progress?.progress_percent ?? 0);

      return {
        ...course,
        progress,
        status,
        percent,
      };
    });
  }, [baseSortedCourses, progressByCourseId]);

  const metrics = useMemo(() => {
    const total = coursesWithProgress.length;
    const completed = coursesWithProgress.filter((c) => c.status === 'completed').length;
    const inProgress = coursesWithProgress.filter((c) => c.status === 'in-progress').length;
    const notStarted = coursesWithProgress.filter((c) => c.status === 'not-started').length;

    return {
      total,
      completed,
      inProgress,
      notStarted,
    };
  }, [coursesWithProgress]);

  const filteredAndSortedCourses = useMemo(() => {
    const filtered = coursesWithProgress.filter((course) => {
      if (statusFilter === 'all') return true;
      return course.status === statusFilter;
    });

    return [...filtered].sort((a, b) => {
      const aName = String(a.fullname ?? a.shortname ?? `Course ${a.id}`);
      const bName = String(b.fullname ?? b.shortname ?? `Course ${b.id}`);

      if (sortBy === 'name-asc') {
        return aName.localeCompare(bName);
      }

      if (sortBy === 'name-desc') {
        return bName.localeCompare(aName);
      }

      if (sortBy === 'progress-desc') {
        return b.percent - a.percent || aName.localeCompare(bName);
      }

      if (sortBy === 'progress-asc') {
        return a.percent - b.percent || aName.localeCompare(bName);
      }

      return getStatusRank(a.status) - getStatusRank(b.status) || aName.localeCompare(bName);
    });
  }, [coursesWithProgress, statusFilter, sortBy]);

  const totalUi = {
    valueColor: 'var(--hbr-color-neutral-text, #23282E)',
    background: 'var(--hbr-color-neutral-bg, #FFFFFF)',
    borderColor: 'var(--hbr-color-neutral-border-weak, #E1E4E8)',
  };

  const completedUi = getStatusUi('completed');
  const inProgressUi = getStatusUi('in-progress');
  const notStartedUi = getStatusUi('not-started');

  if (isLoading) {
    return <div className="p-6">Loading courses…</div>;
  }

  if (error) {
    return <div className="p-6 text-red-600">{(error as Error).message}</div>;
  }

  return (
    <div className="p-6 flex flex-col gap-5">
      <div className="max-w-6xl">
        <h1 className="text-2xl font-semibold">Train the Trainer</h1>
        <p className="text-sm opacity-70 mt-1">
          Select a course to launch the learning experience.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 items-stretch">
        <MetricCard
          title="Total Courses"
          value={metrics.total}
          valueColor={totalUi.valueColor}
          background={totalUi.background}
          borderColor={totalUi.borderColor}
        />

        <MetricCard
          title="Completed"
          value={metrics.completed}
          iconAsset={statusOk}
          iconColor={completedUi.metricIconColor}
          iconBg={completedUi.metricIconBg}
          valueColor={completedUi.metricValueColor}
          background={completedUi.metricBackground}
          borderColor={completedUi.metricBorder}
        />

        <MetricCard
          title="In Progress"
          value={metrics.inProgress}
          iconAsset={statusInProgress}
          iconColor={inProgressUi.metricIconColor}
          iconBg={inProgressUi.metricIconBg}
          valueColor={inProgressUi.metricValueColor}
          background={inProgressUi.metricBackground}
          borderColor={inProgressUi.metricBorder}
        />

        <MetricCard
          title="Not Started"
          value={metrics.notStarted}
          iconAsset={statusGeneralDormant}
          iconColor={notStartedUi.metricIconColor}
          iconBg={notStartedUi.metricIconBg}
          valueColor={notStartedUi.metricValueColor}
          background={notStartedUi.metricBackground}
          borderColor={notStartedUi.metricBorder}
        />
      </div>

      <HbrCard
        style={
          {
            '--width': '100%',
            '--border-radius': '8px',
            '--border-width': '2px',
            '--border-color': 'var(--hbr-color-neutral-border-weak, #E1E4E8)',
            '--box-shadow': 'none',
          } as React.CSSProperties
        }
      >
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setStatusFilter('all')}
            className="rounded-full px-3 py-1 text-sm border"
            style={{
              background:
                statusFilter === 'all'
                  ? 'var(--hbr-color-interact, #1D69CC)'
                  : 'var(--hbr-color-neutral-bg, #FFFFFF)',
              color:
                statusFilter === 'all'
                  ? 'var(--hbr-color-neutral-contrast-text, #FFFFFF)'
                  : 'var(--hbr-color-neutral-text, #23282E)',
              borderColor:
                statusFilter === 'all'
                  ? 'var(--hbr-color-interact, #1D69CC)'
                  : 'var(--hbr-color-neutral-border, #889099)',
            }}
          >
            All
          </button>

          <button
            type="button"
            onClick={() => setStatusFilter('completed')}
            className="rounded-full px-3 py-1 text-sm border"
            style={{
              background:
                statusFilter === 'completed'
                  ? 'var(--hbr-color-success, #45991F)'
                  : 'var(--hbr-color-success-bg, #F4FAEF)',
              color:
                statusFilter === 'completed'
                  ? 'var(--hbr-color-neutral-contrast-text, #FFFFFF)'
                  : 'var(--hbr-color-success, #45991F)',
              borderColor: 'var(--hbr-color-success, #45991F)',
            }}
          >
            Completed
          </button>

          <button
            type="button"
            onClick={() => setStatusFilter('in-progress')}
            className="rounded-full px-3 py-1 text-sm border"
            style={{
              background:
                statusFilter === 'in-progress'
                  ? 'var(--hbr-color-interact, #1D69CC)'
                  : 'var(--hbr-color-info-bg, #EEF5FE)',
              color:
                statusFilter === 'in-progress'
                  ? 'var(--hbr-color-neutral-contrast-text, #FFFFFF)'
                  : 'var(--hbr-color-interact, #1D69CC)',
              borderColor: 'var(--hbr-color-interact, #1D69CC)',
            }}
          >
            In Progress
          </button>

          <button
            type="button"
            onClick={() => setStatusFilter('not-started')}
            className="rounded-full px-3 py-1 text-sm border"
            style={{
              background:
                statusFilter === 'not-started'
                  ? 'var(--hbr-color-neutral, #656C75)'
                  : 'var(--hbr-color-neutral-bg-medium, #F7F7F7)',
              color:
                statusFilter === 'not-started'
                  ? 'var(--hbr-color-neutral-contrast-text, #FFFFFF)'
                  : 'var(--hbr-color-neutral-text, #23282E)',
              borderColor: 'var(--hbr-color-neutral-border, #889099)',
            }}
          >
            Not Started
          </button>

          <div
            className="flex items-center gap-2 ml-auto"
            style={{ minWidth: '260px' }}
          >
            <label
              htmlFor="ttt-sort"
              className="text-sm font-medium whitespace-nowrap"
              style={{ color: 'var(--hbr-color-neutral-text, #23282E)' }}
            >
              Sort by
            </label>

            <select
              id="ttt-sort"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortOption)}
              className="w-full rounded-md border px-3 py-2 bg-white text-sm"
              style={{
                borderColor: 'var(--hbr-color-neutral-border, #889099)',
                color: 'var(--hbr-color-neutral-text, #23282E)',
                background: 'var(--hbr-color-neutral-bg, #FFFFFF)',
              }}
            >
              <option value="status">Completion Status</option>
              <option value="name-asc">Name (A–Z)</option>
              <option value="name-desc">Name (Z–A)</option>
              <option value="progress-desc">Progress (High to Low)</option>
              <option value="progress-asc">Progress (Low to High)</option>
            </select>
          </div>
        </div>

        <div
          className="text-sm mt-3"
          style={{ color: 'var(--hbr-color-neutral-text-weak, #596069)' }}
        >
          Showing {filteredAndSortedCourses.length} of {metrics.total} courses
        </div>
      </HbrCard>

      {isProgressLoading ? (
        <div
          className="text-sm"
          style={{ color: 'var(--hbr-color-neutral-text-weak, #596069)' }}
        >
          Loading course progress…
        </div>
      ) : null}

      {!filteredAndSortedCourses.length ? (
        <HbrCard
          style={
            {
              '--border-radius': '8px',
              '--border-width': '2px',
              '--border-color': 'var(--hbr-color-neutral-border-weak, #E1E4E8)',
              '--box-shadow': 'none',
              '--width': '100%',
            } as React.CSSProperties
          }
        >
          <div
            className="hbr-css__text-secondary-sm"
            style={{ color: 'var(--hbr-color-neutral-text-weak, #596069)' }}
          >
            No Train the Trainer courses match the selected filters.
          </div>
        </HbrCard>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 items-stretch">
          {filteredAndSortedCourses.map((course) => {
            const cleanSummary = truncate(stripHtml(course.summary), 240);
            const progress = course.progress;
            const statusUi = getStatusUi(course.status);

            const percent = Number(progress?.progress_percent ?? 0);
            const completedCount = Number(progress?.completed_count ?? 0);
            const totalCount = Number(progress?.total_count ?? 0);

            return (
              <HbrCard
                key={course.id}
                selectable={true}
                role="button"
                sentiment={statusUi.cardSentiment}
                onClick={() =>
                  navigate(
                    `/learning/${provider}/${instance}/course/${course.id}?fullscreen=1&returnTo=${encodeURIComponent(
                      '/workshops/train-the-trainer'
                    )}`
                  )
                }
                style={
                  {
                    '--width': '100%',
                    '--min-height': '360px',
                    '--max-width': '100%',
                    '--border-radius': '8px',
                    '--border-width': '2px',
                    '--border-color': 'var(--hbr-color-neutral-border-weak, #E1E4E8)',
                    '--box-shadow': 'var(--hbr-shadow-xs)',
                    '--body-padding': '20px',
                    height: '100%',
                  } as React.CSSProperties
                }
              >
                <div className="flex flex-col gap-4 h-full min-h-[320px]">
                  <div slot="header" className="hbr-css__layout-row-sm hbr-css__layout-align-start">
                    <div className="min-w-0 flex-1">
                      <div
                        className="text-lg font-semibold leading-snug break-words"
                        style={{ color: 'var(--hbr-color-neutral-text, #23282E)' }}
                      >
                        {course.fullname ?? course.shortname ?? `Course ${course.id}`}
                      </div>

                      {course.shortname ? (
                        <div
                          className="text-sm mt-1"
                          style={{ color: 'var(--hbr-color-neutral-text-weak, #596069)' }}
                        >
                          {course.shortname}
                        </div>
                      ) : null}
                    </div>

                    <HbrTag size="small" status={true} sentiment={statusUi.sentiment}>
                      <HbrIcon slot="prefix" asset={statusUi.iconAsset} />
                      {statusUi.label}
                    </HbrTag>
                  </div>

                  {cleanSummary ? (
                    <div
                      className="text-sm leading-6"
                      style={{ color: 'var(--hbr-color-neutral-text-medium, #373C42)' }}
                    >
                      {cleanSummary}
                    </div>
                  ) : (
                    <div
                      className="text-sm italic"
                      style={{ color: 'var(--hbr-color-neutral-text-weak, #596069)' }}
                    >
                      No course description available.
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div
                      className="rounded-lg px-3 py-2"
                      style={{
                        background: 'var(--hbr-color-neutral-bg, #FFFFFF)',
                        border: '1px solid var(--hbr-color-neutral-border-weak, #E1E4E8)',
                      }}
                    >
                      <div
                        className="text-xs"
                        style={{ color: 'var(--hbr-color-neutral-text-weak, #596069)' }}
                      >
                        Progress
                      </div>
                      <div
                        className="text-sm font-medium"
                        style={{ color: 'var(--hbr-color-neutral-text, #23282E)' }}
                      >
                        {percent}%
                      </div>
                    </div>

                    <div
                      className="rounded-lg px-3 py-2"
                      style={{
                        background: 'var(--hbr-color-neutral-bg, #FFFFFF)',
                        border: '1px solid var(--hbr-color-neutral-border-weak, #E1E4E8)',
                      }}
                    >
                      <div
                        className="text-xs"
                        style={{ color: 'var(--hbr-color-neutral-text-weak, #596069)' }}
                      >
                        Completed Items
                      </div>
                      <div
                        className="text-sm font-medium"
                        style={{ color: 'var(--hbr-color-neutral-text, #23282E)' }}
                      >
                        {completedCount} / {totalCount}
                      </div>
                    </div>

                    <div
                      className="rounded-lg px-3 py-2"
                      style={{
                        background: 'var(--hbr-color-neutral-bg, #FFFFFF)',
                        border: '1px solid var(--hbr-color-neutral-border-weak, #E1E4E8)',
                      }}
                    >
                      <div
                        className="text-xs"
                        style={{ color: 'var(--hbr-color-neutral-text-weak, #596069)' }}
                      >
                        Status
                      </div>
                      <div
                        className="text-sm font-medium"
                        style={{ color: 'var(--hbr-color-neutral-text, #23282E)' }}
                      >
                        {statusUi.label}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-1">
                    <div
                      className="w-full h-2 rounded-full overflow-hidden"
                      style={{ background: 'var(--hbr-color-neutral-bg-weak, #E1E4E8)' }}
                    >
                      <div
                        className="h-full transition-all"
                        style={{
                          width: `${Math.max(0, Math.min(percent, 100))}%`,
                          background: statusUi.progressColor,
                        }}
                      />
                    </div>

                    <div
                      className="text-xs"
                      style={{ color: 'var(--hbr-color-neutral-text-weak, #596069)' }}
                    >
                      {percent === 100
                        ? 'Course complete'
                        : percent > 0
                          ? `${percent}% complete`
                          : 'Not started yet'}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 pt-1">
                    <span
                      className="text-xs rounded px-2 py-1"
                      style={{
                        background: 'var(--hbr-color-neutral-bg, #FFFFFF)',
                        color: 'var(--hbr-color-neutral-text-weak, #596069)',
                        border: '1px solid var(--hbr-color-neutral-border-weak, #E1E4E8)',
                      }}
                    >
                      Course ID: {course.id}
                    </span>
                    <span
                      className="text-xs rounded px-2 py-1"
                      style={{
                        background: 'var(--hbr-color-neutral-bg, #FFFFFF)',
                        color: 'var(--hbr-color-neutral-text-weak, #596069)',
                        border: '1px solid var(--hbr-color-neutral-border-weak, #E1E4E8)',
                      }}
                    >
                      Category ID: {courseCategoryId(course)}
                    </span>
                    <span
                      className="text-xs rounded px-2 py-1"
                      style={{
                        background: 'var(--hbr-color-neutral-bg, #FFFFFF)',
                        color: 'var(--hbr-color-neutral-text-weak, #596069)',
                        border: '1px solid var(--hbr-color-neutral-border-weak, #E1E4E8)',
                      }}
                    >
                      External Moodle
                    </span>
                  </div>

                  <div
                    className="pt-1 text-sm font-medium mt-auto"
                    style={{ color: 'var(--hbr-color-interact, #1D69CC)' }}
                  >
                    Open training →
                  </div>
                </div>
              </HbrCard>
            );
          })}
        </div>
      )}
    </div>
  );
}